import {
  Box,
  ButtonBase,
  Stack,
  Typography,
  Chip,
  IconButton,
  Tooltip,
} from "@mui/material";
import React from "react";
import { Link } from "react-router-dom";
import styles from "./HomeCard.module.css";
import { CardSpotlight } from "../spotlight/CardSpotLight";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import StarIcon from "@mui/icons-material/Star";
import { useTranslation } from "react-i18next";

interface CustomInputProperties {
  languageResource: string;
  link: string;
  children?: React.ReactNode;
  image: string;
  smallCard?: boolean;
  isNew?: boolean;
  isPopular?: boolean;
  isHighPriority?: boolean;
}

export function HomeCard(properties: CustomInputProperties) {
  var smallCard = properties.smallCard ?? false;
  const isNew = properties.isNew ?? false;
  const isPopular = properties.isPopular ?? false;
  const isHighPriority = properties.isHighPriority ?? false;
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        width: smallCard ? "240px" : "280px",
        maxWidth: smallCard ? "240px" : "280px",
        position: "relative",
      }}
    >
      <Link
        to={properties.link}
        title={t(`home.tools.${properties.languageResource}.tooltip`)}
        style={{ textDecoration: "none" }}
      >
        <ButtonBase
          className={styles.card}
          component="div"
          sx={{
            backgroundColor: "#282B39",
            borderRadius: "24px",
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* Badge for New/Popular */}
          {(isNew || isPopular) && (
            <Box
              sx={{
                position: "absolute",
                top: 12,
                right: 12,
                zIndex: 4,
              }}
            >
              <Chip
                icon={
                  isPopular ? (
                    <StarIcon sx={{ fontSize: 16, color: "#FFFFFF" }} />
                  ) : undefined
                }
                label={isNew ? t("home.badges.new") : t("home.badges.popular")}
                size="small"
                sx={{
                  backgroundColor: isNew ? "#4CAF50" : "#FF9800",
                  color: "white",
                  fontSize: "10px",
                  fontWeight: 600,
                  height: "20px",
                  "& .MuiChip-label": {
                    px: 1,
                  },
                }}
              />
            </Box>
          )}

          <Box sx={{ borderRadius: "24px" }}>
            <CardSpotlight spotlightStrength={0.18} borderRadiusPx={24}>
              {/* Image Container with Overlay */}
              <Box sx={{ position: "relative" }}>
                <img
                  src={properties.image}
                  height={smallCard ? 140 : 180}
                  width={smallCard ? 240 : 280}
                  alt={`${properties.languageResource} Image`}
                  loading={isHighPriority ? "eager" : "lazy"}
                  fetchpriority={isHighPriority ? "high" : "low"}
                  {...(isHighPriority && { fetchpriority: "high" })}
                  style={{
                    borderRadius: "24px 24px 0px 0px",
                    objectFit: "cover",
                    transition: "transform 0.3s ease",
                  }}
                  className={styles.cardImage}
                />

                {/* Gradient Overlay */}
                <Box
                  sx={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: "40%",
                    background:
                      "linear-gradient(180deg, transparent 0%, rgba(40, 43, 57, 0.8) 100%)",
                  }}
                />

                {/* Arrow Icon */}
                <Box
                  sx={{
                    position: "absolute",
                    top: 12,
                    right: 12,
                    opacity: 0,
                    transform: "translateX(-10px)",
                    transition: "all 0.3s ease",
                    zIndex: 2,
                  }}
                  className={styles.arrowIcon}
                >
                  <IconButton
                    size="small"
                    sx={{
                      backgroundColor: "rgba(255, 255, 255, 0.9)",
                      color: "#282B39",
                      "&:hover": {
                        backgroundColor: "rgba(255, 255, 255, 1)",
                        transform: "scale(1.1)",
                      },
                    }}
                  >
                    <ArrowForwardIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              </Box>

              {/* Content */}
              <Stack
                sx={{
                  pl: 2.5,
                  pr: 2.5,
                  pt: 1.5,
                  pb: 2.5,
                  position: "relative",
                  zIndex: 2,
                }}
              >
                <Typography
                  variant="h4"
                  sx={{
                    textOverflow: "ellipsis",
                    WebkitLineClamp: 1,
                    lineClamp: 1,
                    overflow: "hidden",
                    wordBreak: "break-all",
                    WebkitBoxOrient: "vertical",
                    display: "-webkit-box",
                    fontSize: smallCard ? "19px" : "21px",
                    fontWeight: 400,
                    color: "#FFD9A8",
                    mb: 0.5,
                    transition: "color 0.3s ease",
                  }}
                  className={styles.cardTitle}
                >
                  {t(`home.tools.${properties.languageResource}.title`)}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: 13,
                    textOverflow: "ellipsis",
                    WebkitLineClamp: 2,
                    lineClamp: 2,
                    overflow: "hidden",
                    WebkitBoxOrient: "vertical",
                    display: "-webkit-box",
                    color: "#aedff2",
                    lineHeight: 1.4,
                    opacity: 0.9,
                    transition: "opacity 0.3s ease",
                  }}
                  className={styles.cardDescription}
                >
                  {t(`home.tools.${properties.languageResource}.description`)}
                </Typography>
              </Stack>
            </CardSpotlight>
          </Box>
        </ButtonBase>
      </Link>
    </Box>
  );
}
